import React, { useCallback, useState } from 'react';
import emailRegex from '../../_helpers/emailRegex';
import moment from 'moment';
import classnames from 'classnames';
import injectSheet from 'react-jss';

// Components
import LocationAC from '../LocationAC';
import TestLocationCard from '../TestLocationCard';
import Input from '../Input';
import InputRow from '../InputRow';
import baseInputStyles from '../Input/baseInputStyles';
import DateRangePicker from '../DateRangePicker';
import Button from '../Button';
import ModalWrapper from '../ModalWrapper';
import PaymentMethodSection from './PaymentMethodSection';
import TotalCost from './TotalCost';

interface Props {
  classes: any;
  testLocations: any;
}

function IdentifierBacklogForm({ classes, testLocations }: Props) {
  const [form, setForm] = useState({
    nameOnCard: '',
    email: '',
    selectedTestLocation: null,
    startDate: null,
    endDate: moment().subtract(1, 'day'),
    courtId: '',
  });

  const handleChange = useCallback(
    (value: any, key: string) => {
      setForm(prevForm => {
        return { ...prevForm, [key]: value };
      });
    },
    [setForm],
  );

  const { email, nameOnCard, selectedTestLocation, startDate, endDate, courtId } = form;

  const invalidEmail = !!email ? !emailRegex.test(email) : false;
  const invalidEmailMessage = 'Sorry, this email is not valid.';

  const handleSelect = value => {
    handleChange(moment().subtract(1, 'day'), 'endDate');
    handleChange(null, 'startDate');
    handleChange(value, 'selectedTestLocation');
  };

  const isPurchaseValid =
    !invalidEmail && nameOnCard && selectedTestLocation && startDate && endDate;

  return (
    <>
      <InputRow>
        <Input
          header="Select Test Center"
          forId="select-test-center"
          classes={{ root: classes.acInputContainer }}
        >
          <LocationAC
            inputProps={{ id: 'select-test-center' }}
            onSelect={handleSelect}
            className={classes.acInput}
            testLocations={testLocations}
            placeholder=""
            closeOnSelect
            focus
          />
        </Input>
        {selectedTestLocation && <TestLocationCard testLocation={selectedTestLocation} />}
      </InputRow>
      <div className={classnames(classes.faded, { [classes.fadeIn]: selectedTestLocation })}>
        <InputRow>
          <Input header="Select Date Range">
            <DateRangePicker onChange={handleChange} form={form} />
            {!!startDate && !!endDate && <TotalCost form={form} />}
          </Input>
        </InputRow>
      </div>

      <div className={classnames(classes.faded, { [classes.fadeIn]: startDate && endDate })}>
        <InputRow>
          <Input
            forId="email address"
            type="email"
            header="Which email should TestNotify send this information to?"
            onChange={e => handleChange(e.target.value, 'email')}
            error={invalidEmail}
            errorMessage={invalidEmail ? invalidEmailMessage : ''}
          />
        </InputRow>

        <InputRow>
          <Input
            header="Court ID (optional)"
            forId="court-id"
            onChange={e => handleChange(e.target.value, 'courtId')}
          />
        </InputRow>

        <PaymentMethodSection onChange={handleChange} form={form} />
      </div>

      <div
        className={classnames(classes.faded, classes.actions, {
          [classes.fadeIn]: isPurchaseValid,
        })}
      >
        <ModalWrapper type="ConfirmIdentifierLogPayment" modalProps={{ form }}>
          <Button color="moonBlue">Purchase</Button>
        </ModalWrapper>
      </div>
    </>
  );
}

const styles = theme => {
  return {
    acInputContainer: {
      marginTop: theme.spacing.unit * 2,
      width: '100%',
      '& > div:last-child': {
        display: 'block !important',
      },
    },
    acInput: {
      ...baseInputStyles(theme),
      width: '100%',
    },
    faded: {
      transition: '.4s opacity',
      pointerEvents: 'none',
      opacity: 0.1,
    },
    fadeIn: {
      opacity: '1 !important',
      pointerEvents: 'initial !important',
    },
    actions: {
      display: 'flex',
      width: '100%',
      justifyContent: 'flex-end',
    },
  };
};

export default injectSheet(styles)(IdentifierBacklogForm);
